import React from 'react';
import Divider from '~/components/atom/Divider';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Heading4 } from '~/components/atom/Typography';
import Markdown from '~/components/molecule/Markdown';
import {
  Lfa_Service_Acceptance_Type,
  type Lfa_Conversion_Data_Rejection_Criteria,
} from '~/graphql/types';
import useAddToast from '~/hooks/useAddToast';
import useErrorReporter from '~/hooks/useErrorReporter';
import formatToastMessage from '~/util/formatToastMessage';
import type { ValidationError } from '~/components/page/LFA/state/validationState';
import type { FeedbackType, UpdateFeedbackArgs } from '../../utils/reducer';
import Catalog from '~/Catalog';
import FeedbackForm from '../FeedbackForm';
import {
  appointmentAnswerOptions,
  conversionAnswerOptions,
} from '~/components/page/LFA/constants';
import Button from '~/components/atom/Button';
import type { UseServiceFeedbackType } from '../../hooks/useServiceFeedback';
import type { ClientLfaConversion } from '~/components/page/LFA/types';

const text = {
  didBecomeCustomer: 'Is de persoon klant geworden?',
  areCriteriaMet: 'Voldeed deze afspraak aan onze criteria?',
};

export type Props = Pick<
  UseServiceFeedbackType,
  'clearValidationErrorForField'
> & {
  dataTestId?: string;
  className?: string;
  error?: ValidationError;
  service: ClientLfaConversion['selectedServices'][number];
  serviceFeedback: FeedbackType;
  updateLocalFeedback: (feedback: UpdateFeedbackArgs) => void;
  onRemoveService?: () => void;
};

const ServiceBlock: React.FCC<Props> = ({
  dataTestId,
  className,
  service,
  serviceFeedback,
  updateLocalFeedback,
  error,
  clearValidationErrorForField,
  onRemoveService,
}) => {
  const { name, description, acceptance } = service;

  const errorReporter = useErrorReporter();
  const addToast = useAddToast();

  if (!acceptance[0]) {
    errorReporter.captureException(
      new Error(
        `Service block has no acceptance ${JSON.stringify(service, null, 2)}`,
      ),
      'error',
    );
    addToast([
      formatToastMessage(Catalog.genericUnknownErrorMessage, 'danger'),
    ]);

    return null;
  }

  return (
    <JustificationContainer
      dataTestId={dataTestId}
      className={className}
      width="100%"
      border={{
        radius: 'base',
        width: 's',
        color: { group: error ? 'danger' : 'tertiary' },
      }}
      padding={['m', 'l']}
      backgroundColor={
        error ? { group: 'danger', variant: 'translucent' } : undefined
      }
      direction="column"
    >
      <JustificationContainer
        width="100%"
        justification="space-between"
        align="center"
        margin={[null, null, onRemoveService ? 's' : 'xs', null]}
      >
        <Heading4 color={{ group: 'secondary' }} margin={[null]}>
          {name}
        </Heading4>
        {onRemoveService && (
          <Button
            ghost
            appearance="danger"
            icon="delete"
            onClick={onRemoveService}
          />
        )}
      </JustificationContainer>
      {description && <Markdown value={description} />}

      <ServiceContentFacade
        error={error}
        clearValidationErrorForField={clearValidationErrorForField}
        serviceFeedback={serviceFeedback}
        service={service}
        updateLocalFeedback={updateLocalFeedback}
      />
    </JustificationContainer>
  );
};

export type ServiceFacadeProps = {
  dataTestId?: string;
  className?: string;
  error?: ValidationError;
  rejectionCriteria: Array<Lfa_Conversion_Data_Rejection_Criteria>;
  serviceId: string;
  serviceVersion: number;
  serviceFeedback: FeedbackType;
  updateLocalFeedback: (feedback: UpdateFeedbackArgs) => void;
  clearValidationErrorForField: (args: { key: string; field: string }) => void;
};

const ServiceContentFacade: React.FCC<{
  serviceFeedback: FeedbackType;
  service: ClientLfaConversion['selectedServices'][number];
  error?: ValidationError;
  clearValidationErrorForField: (args: { key: string; field: string }) => void;
  updateLocalFeedback: (feedback: UpdateFeedbackArgs) => void;
}> = ({
  service,
  updateLocalFeedback,
  serviceFeedback,
  error,
  clearValidationErrorForField,
}) => {
  const content = service.acceptance
    .map(acceptance => {
      const props: ServiceFacadeProps = {
        error,
        clearValidationErrorForField,
        serviceVersion: service.version,
        serviceId: service.id,
        serviceFeedback,
        updateLocalFeedback,
        rejectionCriteria: acceptance.reason,
      };

      switch (acceptance.type) {
        case Lfa_Service_Acceptance_Type.Conversion:
          return (
            <FeedbackForm
              {...props}
              key={service.id}
              feedbackType={Lfa_Service_Acceptance_Type.Conversion}
              label={text.didBecomeCustomer}
              answerOptions={conversionAnswerOptions}
            />
          );

        case Lfa_Service_Acceptance_Type.Appointment: {
          if (acceptance.reason.length === 0) {
            return null;
          }
          return (
            <FeedbackForm
              {...props}
              key={service.id}
              feedbackType={Lfa_Service_Acceptance_Type.Appointment}
              label={text.areCriteriaMet}
              answerOptions={appointmentAnswerOptions}
            />
          );
        }
      }
    })
    .filter(Boolean);

  if (content.length === 0) return null;

  return (
    <JustificationContainer direction="column" width="100%" gap="xs">
      <Divider margin={['xxs', null, 'xxs', null]} />
      {content}
    </JustificationContainer>
  );
};

export default ServiceBlock;
